import * as React from "react";
import { GridColumns, GridRenderCellParams } from "@mui/x-data-grid-pro";
import { PersonGrandCentralDocument } from "../../../../services/types/search.service.types";
import CclPersistentDataGrid from "../../../../components/common/cclPersistentDataGrid/cclPersistentDataGrid";
import { useSelector } from "react-redux";
import { updateApplicationUsersSearchGridState } from "../../../../app/slices/applicationUsersSearchListTableStateSlice";
import { UserClaimsService } from "../../../../services/currentUserService/currentUserService";
import { addCustomDataGridFilters } from "../../../../components/common/customDataGridFilters/customDataGridFilters";
import CclCommonLink from "../../../../components/common/cclCommonLink";

export interface ApplicationUsersDataGridProps {
    users: PersonGrandCentralDocument[];
    isLoading: boolean;
    hideToolbar?: boolean;
    showResultBar?: boolean;
    resultBarText?: string;
    editButtonHandler: (userEmail: string) => void;
    editButtonLoading: boolean;
}

export const ApplicationUsersDataGrid: React.FC<ApplicationUsersDataGridProps> = (props) => {
    const [selectedRow, setSelectedRow] = React.useState<any>(null);

    const stateSelector = useSelector((state: any) => state.applicationUsersSearchListTableState);
    const claimsService = new UserClaimsService();
    const isSuperAdmin = claimsService.IsUserSuperAdmin();
    function getFullName(params: any) {
        const firstName = params.row.firstName ? params.row.firstName.trim() : "";
        const lastName = params.row.lastName ? params.row.lastName.trim() : "";
        if (!firstName && !lastName) {
            return "";
        }
        return `${firstName} ${lastName}`;
    }

    function selectBestImKey(doc: PersonGrandCentralDocument): string {
        // Extract all numeric identities from AccountIdentity[]
        const numericIdentities = (doc.identities || [])
            .map((idObj) => idObj.identity)
            .filter((id) => /^\d+$/.test(id));

        if (numericIdentities.length === 1) {
            return numericIdentities[0];
        }

        if (numericIdentities.length > 1) {
            if (doc.imKey && numericIdentities.includes(doc.imKey)) {
                return doc.imKey;
            }
            // Return the greatest numeric identity (as a number)
            return numericIdentities.reduce((max, curr) =>
                BigInt(curr) > BigInt(max) ? curr : max
            );
        }

        if (doc.imKey) {
            return doc.imKey;
        }

        return "0";
    }

    const COLUMNS = [
        {
            field: "imKey",
            headerName: "ID",
            valueGetter: (params: any) => {
                return selectBestImKey(params.row);
            },
            renderCell: (params: GridRenderCellParams) =>
                params.value !== "0" ? (
                    <CclCommonLink
                        keyVal={`${params.value}-id`}
                        text={params.value}
                        to={`/user?email=${encodeURIComponent(
                            params.row.accountEmails[0].email
                        )}&imkey=${params.value}`}
                    />
                ) : (
                    ""
                ),
            width: 150,
        },
        {
            field: "name",
            headerName: "User",
            valueGetter: getFullName,
            renderCell: (params: GridRenderCellParams) => {
                var selectedImKey = selectBestImKey(params.row);
                return selectedImKey !== "0" ? (
                    <CclCommonLink
                        keyVal={`${params.value}-user`}
                        text={params.value}
                        to={`/user?email=${encodeURIComponent(
                            params.row.accountEmails[0].email
                        )}&imkey=${selectedImKey}`}
                    />
                ) : (
                    ""
                );
            },
            width: 450,
        },
        {
            field: "accountEmails",
            headerName: "Email",
            valueGetter: (params: any) => {
                return params.row.accountEmails[0].email;
            },
            renderCell: (params: GridRenderCellParams) => (
                <CclCommonLink
                    text={params.value}
                    keyVal={params.value}
                    to={`mailto:${params.value}`}
                    type={"email"}
                />
            ),
            width: 220,
        },
        {
            field: "Enabled",
            headerName: "Enabled",
            width: 150,
            valueGetter: (params: any) => {
                return params.row.accountApplications[0].applicationEnabled ? "Yes" : "No";
            },
        },
        {
            field: "accountApplications",
            headerName: "Groups",
            flex: 1,
            valueGetter: (params: any) => {
                return params.row.accountApplications.length > 0
                    ? params.row.accountApplications[0].applicationGroups
                          .map((x: { groupName: any }) => x.groupName)
                          .join(", ")
                    : "";
            },
        },
    ];

    return (
        <CclPersistentDataGrid
            columns={addCustomDataGridFilters(COLUMNS as GridColumns<any>)}
            data={props.users}
            loading={props.isLoading}
            stateSelector={stateSelector}
            userPreferenceId={12}
            getRowIdFn={(row: PersonGrandCentralDocument) => selectBestImKey(row)}
            actionCreator={updateApplicationUsersSearchGridState}
            hideToolbar={props.hideToolbar === true}
            initState={JSON.stringify({
                sorting: { sortModel: [{ field: "email", sort: "asc" }] },
            })}
            miscButtons={
                isSuperAdmin
                    ? [
                          {
                              id: "edit-app-user",
                              caption: "Edit Access",
                              onClick: () =>
                                  props.editButtonHandler(selectedRow[0].accountEmails[0].email),
                              restricted: false,
                              returnSelected: true,
                              disable: !selectedRow || selectedRow?.length !== 1,
                              loadingBtn: true,
                              isLoading: props.editButtonLoading,
                          },
                      ]
                    : []
            }
            resultBar={props.showResultBar ?? false}
            resultBarText={props.resultBarText ?? ""}
            showCheckboxes={true}
            onSelectedRowsChange={(rows: any[] | undefined) => setSelectedRow(rows)}
            hideFooter={false}
        />
    );
};

export default ApplicationUsersDataGrid;
